import React from "react";
import { useEffect, useRef } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  MenuItem,
  Menu,
  TextField,
  InputAdornment,
  Avatar,
  Divider,
  ListItemIcon,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { useSelector, useDispatch } from "react-redux";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { useNavigate } from "react-router-dom";
import {
  getSearchOrdersRequest,
  resetSearchResults,
} from "../../redux/orders/slice";
import SearchResults from "../search-results/search-results";

export default function MenuAppBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [auth, setAuth] = React.useState(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = React.useState("");
  const [searchAnchorEl, setSearchAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const open = Boolean(anchorEl);
  const { currentUser } = useSelector((state: any) => state.user.user);
  const { searchLoading, searchResults } = useSelector(
    (state: any) => state.orders
  );

  useEffect(() => {
    //==> Had to add to build w/o errors
    setAuth(true);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (searchValue.trim() && searchInputRef.current) {
      if (searchLoading || searchResults !== null) {
        setSearchAnchorEl(searchInputRef.current);
      }
    }
  }, [searchResults, searchValue, searchLoading]);

  // const handleChange = (event) => {
  //   setAuth(event.target.checked);
  // };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyProfileClick = () => {
    setAnchorEl(null);
    navigate("/myprofile");
  };

  const onSignOut = () => {
    setAnchorEl(null);
    localStorage.removeItem("accessToken");
    navigate("/signin");
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim()) {
      // Clear previous search results when typing a new search term
      dispatch(resetSearchResults());
      searchTimeoutRef.current = setTimeout(() => {
        dispatch(
          getSearchOrdersRequest({
            searchTerm: value.trim(),
          })
        );
        // Set anchor when API call is made
        if (searchInputRef.current) {
          setSearchAnchorEl(searchInputRef.current);
        }
      }, 1500);
    } else {
      // Clear results when search field is emptied
      dispatch(resetSearchResults());
      setSearchAnchorEl(null);
    }
  };

  const handleSearchFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!searchValue.trim()) {
      // Reset search results when clicking on empty search field
      dispatch(resetSearchResults());
      setSearchAnchorEl(null);
    } else if (searchResults) {
      setSearchAnchorEl(event.currentTarget);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setSearchAnchorEl(null);
    }, 200);
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      // Prevent navigation when Enter is pressed
      return;
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchAnchorEl(null);
    }
  };

  const handleOrderClick = (order: any) => {
    const orderData = {
      id: order.id,
      orderNumber: order.orderNumber,
    };
    navigate(`/orders/Order #(${order.orderNumber})`, { state: { order: orderData } });
    setSearchAnchorEl(null);
    setSearchValue("");
  };

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "#00174F",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            sx={{ height: 35, width: 113, mt: 0.5 }}
            src={require("../../assets/Vision_Logo_white.png")}
          />
          {auth && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{ position: "relative" }}
              >
                <TextField
                  inputRef={searchInputRef}
                  sx={{
                    backgroundColor: "white",
                    height: 40,
                    width: 350,
                    mr: 1,
                    mt: 0.5,
                    borderRadius: 1,
                    ".MuiOutlinedInput-root": {
                      height: 40,
                      paddingTop: 0,
                      paddingBottom: 0,
                    },
                  }}
                  id="search"
                  variant="outlined"
                  placeholder="Search Order ID or Loan #"
                  value={searchValue}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleSearchKeyDown}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlinedIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {Boolean(searchAnchorEl) && (
                  <SearchResults
                    loading={searchLoading}
                    searchResults={searchResults}
                    searchValue={searchValue}
                    onOrderClick={handleOrderClick}
                  />
                )}
              </Box>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
                <Typography sx={{ ml: 1 }}>
                  {currentUser
                    ? `${currentUser?.firstName} ${currentUser?.lastName}`
                    : ""}
                </Typography>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleClose}>
                  <Avatar />
                  <Box sx={{ ml: 1 }}>
                    <Typography>
                      {currentUser &&
                        `${currentUser.firstName} ${currentUser.lastName}`}
                    </Typography>
                    <Typography>
                      {currentUser && `${currentUser.username}`}
                    </Typography>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleMyProfileClick}>
                  <ListItemIcon>
                    <PersonOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  My Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={onSignOut}>
                  <ListItemIcon>
                    <LogoutOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
    </Box>
  );
}
